import { Link } from 'react-router-dom';
import { GlassNav } from '../../components/layout/GlassNav';
import { Button } from '../../components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { GOOGLE_OAUTH_URL } from '../../lib/constants';

export function LandingPage() {
  return (
    <div className="min-h-screen w-full bg-surface text-on-surface">
      <GlassNav />
      
      <section className="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 text-left sm:gap-16 sm:px-6 sm:py-20 lg:flex-row lg:items-center lg:gap-24 lg:px-8 lg:py-32">
        <div className="flex-1 space-y-8 sm:space-y-10">
          <div className="space-y-6">
            <p className="label-md text-secondary">
              Your taxes, handled with care
            </p>
            <h1 className="text-4xl font-bold leading-tight text-primary sm:text-5xl lg:text-6xl xl:text-7xl">
              Expert tax preparation you can trust, from start to finish.
            </h1>
            <p className="max-w-2xl text-lg leading-relaxed text-on-surface/70 sm:text-xl">
              Upload your documents securely, track your filing progress, and sign
              everything online. We handle the details so you don't have to.
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto">Get started</Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">Sign in</Button>
            </Link>
            <a href={GOOGLE_OAUTH_URL} className="w-full sm:w-auto">
              <Button size="lg" variant="ghost" className="w-full sm:w-auto">
                <span className="material-symbols-outlined text-xl">login</span>
                Continue with Google
              </Button>
            </a>
          </div>
        </div>
        
        <div className="flex-1">
          <Card className="bg-surface-container-low">
            <CardHeader>
              <CardTitle className="text-2xl">How it works</CardTitle>
              <CardDescription className="text-base">
                A simple, secure process from document collection to filed return.
              </CardDescription>
            </CardHeader>
            <ul className="space-y-4 text-base text-on-surface/80">
              <li className="flex items-start gap-4">
                <span className="material-symbols-outlined flex-shrink-0 text-2xl text-secondary">verified</span>
                <span>Create your account and connect with your dedicated tax preparer</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="material-symbols-outlined flex-shrink-0 text-2xl text-secondary">cloud_upload</span>
                <span>Upload T4s, receipts, and other documents securely from any device</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="material-symbols-outlined flex-shrink-0 text-2xl text-secondary">timeline</span>
                <span>Track your filing progress in real time as we prepare your return</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="material-symbols-outlined flex-shrink-0 text-2xl text-secondary">draw</span>
                <span>Review and e-sign your T183 authorization form right in your browser</span>
              </li>
            </ul>
          </Card>
        </div>
      </section>

      <section className="border-t border-outline-variant/20 bg-surface-container py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 text-center">
            <div className="space-y-4">
              <p className="label-md text-secondary">Why Mopion?</p>
              <h2 className="text-3xl font-bold text-primary sm:text-4xl lg:text-5xl">
                Your information is safe with us
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-on-surface/70">
                We take the security of your personal and financial information seriously, 
                so you can focus on what matters most.
              </p>
            </div>
            
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="bg-surface-container-lowest text-left">
                <CardHeader>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                    <span className="material-symbols-outlined text-3xl text-primary">lock</span>
                  </div>
                  <CardTitle>Bank-Level Encryption</CardTitle>
                  <CardDescription>
                    Your tax documents and personal details are protected with the same 
                    encryption standards used by major financial institutions.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-surface-container-lowest text-left">
                <CardHeader>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary/10">
                    <span className="material-symbols-outlined text-3xl text-secondary">history</span>
                  </div>
                  <CardTitle>CRA-Compliant Process</CardTitle>
                  <CardDescription>
                    Every step of your filing is documented and meets Canada Revenue Agency 
                    requirements, giving you complete peace of mind.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-surface-container-lowest text-left">
                <CardHeader>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-tertiary/20">
                    <span className="material-symbols-outlined text-3xl text-primary">shield</span>
                  </div>
                  <CardTitle>Privacy First</CardTitle>
                  <CardDescription>
                    Your Social Insurance Number and sensitive data are never exposed or 
                    stored unnecessarily. Your privacy is our priority.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-outline-variant/20 bg-surface-container py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-on-surface/60">
              © {new Date().getFullYear()} Mopion Inc. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/about" className="text-on-surface/60 transition-colors hover:text-primary">
                About
              </Link>
              <Link to="/contact" className="text-on-surface/60 transition-colors hover:text-primary">
                Contact
              </Link>
              <Link to="/login" className="text-on-surface/60 transition-colors hover:text-primary">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
